import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Video, Trash2, Search, CalendarClock, Pencil } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { OnlineStatusBadge } from '@/components/cameras/OnlineStatusBadge'
import { CameraEditSheet } from '@/components/cameras/CameraEditSheet'
import { camerasApi, adminApi } from '@/api/endpoints'
import { avatarColor, cn } from '@/lib/utils'
import { useDirectionStore } from '@/store/directionStore'
import type { Camera } from '@/types'

/**
 * Superuser-only "All Cameras" admin page (#095).
 * Lists every camera across all owners (via GET /cameras/?scope=all), with an owner
 * column, owner filter, search, and per-row delete + "view events" drill-down.
 * Route is gated by <SuperuserRoute>; the backend independently enforces is_superuser.
 */
export function AllCamerasPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const d = useDirectionStore((s) => s.direction)
  const [ownerId, setOwnerId] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<Camera | null>(null)
  const [editId, setEditId] = useState<number | null>(null)

  const { data: users = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.listUsers(),
  })

  const { data: cameras = [], isLoading } = useQuery({
    queryKey: ['all-cameras', ownerId, search],
    queryFn: () => camerasApi.getAllAdmin(ownerId === 'all' ? undefined : Number(ownerId), search || undefined),
  })

  // The slim admin list omits settings fields, so re-fetch the full camera to edit it.
  const { data: editCamera } = useQuery({
    queryKey: ['camera', editId],
    queryFn: () => camerasApi.getById(editId as number),
    enabled: editId != null,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => camerasApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['all-cameras'] })
      toast.success('Camera deleted')
      setDeleteTarget(null)
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Delete failed'
      toast.error(detail)
    },
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className={cn('text-heading', d === 'expressive' && 'font-display')}>All Cameras</h1>
        <p className="text-body text-muted-foreground mt-0.5">Every camera across all users (admin)</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" placeholder="Search name, IMEI, location" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={ownerId} onValueChange={setOwnerId}>
          <SelectTrigger className="w-[240px]"><SelectValue placeholder="All owners" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All owners</SelectItem>
            {users.map(u => (
              <SelectItem key={u.id} value={String(u.id)}>@{u.username} · {u.email}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className={cn('border-border/60', d === 'expressive' && 'rounded-2xl overflow-hidden')}>
        <CardHeader className="pb-3">
          <CardTitle className={cn('text-body font-semibold flex items-center gap-2', d === 'expressive' && 'font-display')}>
            <Video className="h-4 w-4 text-muted-foreground" />
            <span className={cn(d === 'command' && 'font-mono tabular-nums')}>
              {cameras.length} camera{cameras.length === 1 ? '' : 's'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : cameras.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No cameras found</div>
          ) : (
            <div className={cn((d === 'saas' || d === 'command') && 'divide-y divide-border')}>
              {cameras.map((c, idx) => (
                <div
                  key={c.id}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 transition-colors',
                    idx > 0 && d === 'current' && 'border-t border-border/40',
                    d === 'current' && 'hover:bg-muted/40',
                    d === 'command' && 'hover:bg-accent/40 px-3 py-1.5 font-mono text-[12px]',
                    d === 'saas' && 'hover:bg-muted px-3 py-1.5 text-[13px]',
                    d === 'expressive' && 'hover:bg-muted/30',
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={cn('text-sm font-medium truncate',
                        d === 'command' && 'font-mono text-[12px]',
                      )}>
                        {c.name}
                      </span>
                      <OnlineStatusBadge isOnline={c.is_online} lastSeen={c.last_seen} />
                    </div>
                    <p className={cn('text-xs text-muted-foreground truncate',
                      d === 'command' && 'font-mono text-[11px]',
                    )}>
                      IMEI {c.imei}{c.location ? ` · ${c.location}` : ''}
                    </p>
                  </div>

                  {/* Owner */}
                  <div className="hidden sm:flex items-center gap-2 flex-shrink-0 w-52 min-w-0">
                    <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${avatarColor(c.user_username || '?')}`}>
                      {(c.user_username || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">@{c.user_username || '—'}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{c.user_email || '—'}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="View events" onClick={() => navigate(`/events?camera=${c.id}`)}>
                      <CalendarClock className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit camera" onClick={() => setEditId(c.id)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/8" title="Delete camera" onClick={() => setDeleteTarget(c)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
        title="Delete camera"
        description={`Delete "${deleteTarget?.name}" (owner @${deleteTarget?.user_username})? This removes all its events, media, and S3 files.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />

      {editCamera && (
        <CameraEditSheet
          camera={editCamera}
          open={editId != null}
          onOpenChange={(open) => {
            if (!open) {
              setEditId(null)
              // CameraEditSheet invalidates the standard camera keys on save; refresh the
              // admin list too so an edit to another user's camera shows immediately.
              qc.invalidateQueries({ queryKey: ['all-cameras'] })
            }
          }}
        />
      )}
    </div>
  )
}
